import React, { useEffect, useState } from 'react';
import { Product } from '../../../../../sp-types/productType';
import { ProductModifyInputFields } from '../AddNewPRoductModify/components/ProductModifyInputFields';
import { ProductModifyAdvanced } from '../AddNewPRoductModify/components/ProductModifyAdvanced';
import { IDataItem, MaterialDataCard } from '../../../../../components/UI-Elements/Grids/MaterialDataGrid';
import { useMaterialsStore } from '../../../../../components/Stores/MaterialsStore';
import { useAccountStore } from '../../../../../components/Stores/accountStore';
import { formatToPriceInDollars } from '../../../../../utils/FormatValue';
import { Material } from '../../../../../sp-types/materialType';
import ProductChart from './ProductSummaryGraph';
import ButtonDesigned from '../../../../../components/UI-Elements/Buttons/ButtonDesigned';
import { ViewDocumentOverlay } from '../../../../../components/UI-Elements/ViewDocumentOverlay';
import { getFileByUrl } from '../../../../../sp-firebase/storageFB';
import { getUnitObj } from '../../../../../utils/MassUnits/massUnitsUtils';
import { useCurrencyRates } from '@hooks/useCurrencyRates';
interface Props {
  product: Product;
  setProduct: React.Dispatch<React.SetStateAction<Product>>;
  routeToModifyProduct: () => void;
  mainTitle?: string;
  description?: string;
}

export const AddNewProductDetailsSummary: React.FC<Props> = ({
  product,
  routeToModifyProduct,
  setProduct,
  description = 'Product details',
  mainTitle = 'Settings create product',
}) => {
  const { filesUrl, currentPrice } = product;
  const [showContractOverlay, setShowContractOverlay] = useState(false);
  const { materialColorDictionary } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const { pricesMap } = useCurrencyRates();

  const [fileDBUrl, setFileDBUrl] = useState('');

  useEffect(() => {
    const fetchDocument = async () => {
      if (filesUrl.length > 0) {
        const docUrl = await getFileByUrl(filesUrl[0]);
        if (typeof docUrl === 'string') {
          setFileDBUrl(docUrl);
        }
      } else {
        setFileDBUrl('');
      }
    };
    fetchDocument();
  }, [filesUrl]);

  if (!materialsMap || !materialColorDictionary || Object.keys(materialsMap).length === 0)
    return <div> Refresh Page</div>;

  return (
    <div className="flex flex-col flex-1 h-full px-11 py-6 gap-10  ">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-xl">{mainTitle}</p>
          <p className="text-sm text-gray-700">{description}</p>
        </div>
        <ProductModifyInputFields product={product} setProduct={setProduct} isEditable={false} />
      </div>
      <div className="flex flex-1 flex-col justify-evenly overflow-auto">
        <div className="flex flex-row gap-4 overflow-hidden">
          <div className="basis-2/3 overflow-auto flex-grow flex-shrink-0">
            <MaterialDataCard
              materialsMap={materialsMap}
              materialColorDictionary={materialColorDictionary}
              materialsData={getMaterialCardData(product, materialsMap, pricesMap)}
            />
          </div>
          <div className="max-h-[15rem] basis-1/3 flex-grow flex-shrink-0">
            <ProductChart
              productPricePerUnit={currentPrice || 0}
              materialsData={getGraphMaterialsData(product, materialsMap, pricesMap, materialColorDictionary)}
            />
          </div>
        </div>
        <div className=" ">
          <ProductModifyAdvanced />
        </div>
      </div>
      <div className="flex flex-row gap-3">
        <ButtonDesigned className="py-2 px-2" style="outline" onButtonClick={() => routeToModifyProduct()}>
          <div className="flex gap-2">
            <img src="../assets/icons/blue-flat/modify-pencil.svg" />
            Modify
          </div>
        </ButtonDesigned>
        <ButtonDesigned
          style="outline"
          className="py-2 px-2"
          disableButton={!fileDBUrl}
          onButtonClick={() => setShowContractOverlay(true)}
        >
          <div className="flex gap-2 ">
            <img src="../assets/icons/blue-flat/document.svg" />
            View contract
          </div>
        </ButtonDesigned>
      </div>
      {showContractOverlay && fileDBUrl && (
        <ViewDocumentOverlay
          fileUrl={fileDBUrl}
          uploadedDocumentPath={filesUrl[0]}
          setShowOverlay={setShowContractOverlay}
        />
      )}
    </div>
  );
};

const getMaterialCardData = (
  product: Product,
  materialsMap: Record<string, Material>,
  pricesMap: Record<string, number>,
): {
  materialId: string;
  materialNameSize?: number;
  data: (React.ReactNode | string | IDataItem)[];
}[] => {
  return product.ingredients.map((item) => ({
    materialId: item.materialID,
    materialNameSize: 120,
    data: [
      {
        title: 'Weight',
        valueAsString: item.weightInProduct.toLocaleString(undefined, {
          maximumFractionDigits: 4,
          minimumFractionDigits: 0,
        }),
        suffix: getUnitObj(materialsMap[item.materialID]?.unit).shortForm,
        size: 100,
      },
      {
        title: 'Material Price',
        valueAsString: formatToPriceInDollars(pricesMap[item.materialID] || 0),
      },
      {
        title: 'Price in product',
        valueAsString: formatToPriceInDollars(pricesMap[item.materialID] * item.weightInProduct || 0),
      },
    ],
  }));
};

const getGraphMaterialsData = (
  product: Product,
  materialsMap: Record<string, Material>,
  pricesMap: Record<string, number>,
  colorsDictionary: Record<string, string>,
) => {
  return product.ingredients.map((item) => ({
    name: materialsMap[item.materialID].name,
    color: colorsDictionary[item.materialID],
    pricePerUnit: item.weightInProduct * pricesMap[item.materialID],
  }));
};
